import React, { forwardRef, useContext, useEffect, useState } from 'react';
import {
  INotificationCenterProps,
  NotificationCenter,
  NotificationStatus,
} from '@coherence-design-system/notification-center';
import { IContextualMenuProps, IPanel } from '@fluentui/react';

import useFetchNotifions from 'src/notifications/hooks/useFetchNotifications';
import useDismissNotification from 'src/notifications/hooks/useDismissNotification';
import { INotificationPanelProps } from '../../models/INotificationPanelProps';
import { NotificationsContext } from 'src/notifications/context/NotificationsContext';
import { INotificationConfig } from 'src/notifications/models/INotificationConfig';

const NotificationPanel = forwardRef(
  (props: INotificationPanelProps & INotificationConfig, ref: React.RefObject<IPanel>) => {
    const {
      user,
      apiToken,
      coreApiToken,
      graphToken,
      userEmail,
      notificationApiUrl,
      dismissApiUrl,
      updateNotificationCount,
    } = props;
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
    const { setLegacyNotifications, setNotificationConfig } = useContext(NotificationsContext);

    useEffect(() => {
      setNotificationConfig({
        user: user,
        apiToken: apiToken,
        coreApiToken: coreApiToken,
        graphToken: graphToken,
        userEmail: userEmail,
        notificationApiUrl: notificationApiUrl,
        dismissApiUrl: dismissApiUrl,
      });
    }, [
      apiToken,
      coreApiToken,
      dismissApiUrl,
      graphToken,
      notificationApiUrl,
      user,
      setNotificationConfig,
      userEmail,
    ]);

    useEffect(() => {
      setIsPanelOpen(props.notificationIconClicked);
    }, [props.notificationIconClicked]);

    const onUndoItem: INotificationCenterProps['onUpdateNotificationItem'] = (
      itemKey,
      displayStatus
    ) => {
      updateNotificationStatus(itemKey, displayStatus);
    };

    const onDismissItem: INotificationCenterProps['onUpdateNotificationItem'] = (
      itemKey,
      displayStatus
    ) => {
      if (displayStatus === NotificationStatus.DELETED) {
        updateNotificationStatus(itemKey, displayStatus);
      }

      setTimeout(() => {
        const currStatus = getNotificationStatus(itemKey);
        if (currStatus === NotificationStatus.DELETED) {
          dismiss([itemKey]);
        }
      }, 5000);
    };

    const notificationApiAuthToken = `Bearer ${apiToken}`;
    const notificationEmail = user?.email ?? '';

    // TODO: handle loading and error states
    const {
      categories,
      handleAfterDismiss,
      getAllNotificationIds,
      updateNotificationStatus,
      getNotificationStatus,
      legacyNotifications,
    } = useFetchNotifions({
      apiUrl: notificationApiUrl,
      authToken: notificationApiAuthToken,
      coreApiToken: coreApiToken,
      graphToken: graphToken,
      email: notificationEmail,
      updateNotificationCount: updateNotificationCount,
      onDismissItem: onDismissItem,
    });

    // TODO: handle loading and error states
    const { dismiss } = useDismissNotification({
      apiUrl: dismissApiUrl,
      authToken: notificationApiAuthToken,
      coreApiToken: coreApiToken,
      graphToken: graphToken,
      userId: userEmail,
      handleDismiss: handleAfterDismiss,
    });

    const hasNotifications = getAllNotificationIds()?.length > 0;

    const panelActions: IContextualMenuProps = {
      items: [
        {
          key: 'DELETE_ALL',
          text: 'Dismiss All',
          title: 'Dismiss All',
          ariaLabel: 'Dismiss All',
          iconProps: { iconName: 'Delete' },
          onClick: () => {
            dismiss(getAllNotificationIds());
          },
        },
      ],
    };

    useEffect(() => {
      if (legacyNotifications.length === 0) {
        setIsPanelOpen(false);
      }
      setLegacyNotifications(legacyNotifications);
    }, [legacyNotifications, setLegacyNotifications]);

    return (
      <NotificationCenter
        componentRef={ref}
        isOpen={isPanelOpen}
        onDismiss={() => {
          setIsPanelOpen(false);
          props.handleNotificationIconClick(props.notificationIconClicked);
        }}
        isBlocking={false}
        closeButtonAriaLabel={'Close'}
        categories={categories}
        onUpdateNotificationItem={onUndoItem}
        panelActions={hasNotifications ? panelActions : undefined}
        styles={{
          subComponentStyles: {
            panel: {
              content: { backgroundColor: 'white' },
              scrollableContent: { backgroundColor: 'white' },
            },
            emptyState: {
              root: {
                marginTop: '20%',
                backgroundColor: 'white',
                'svg': {
                  width: 120,
                  height: 120,
                },
              },
            },
          },
        }}
      />
    );
  }
);

NotificationPanel.displayName = 'NotificationPanel';

export { NotificationPanel };
// Generated by GitHub Copilot | Updated on: 2026-06-15 | 10 lines modified | Reduced notification empty state icon size from 178x159 to 140x120
// Generated by GitHub Copilot | Updated on: 2026-06-17 | 1 lines modified | Reduced empty state marginTop from 50% to 20%
// Generated by GitHub Copilot | Updated on: 2026-06-23 | 2 lines modified | Hide Dismiss All menu when there are no notifications
// Generated by GitHub Copilot | Updated on: 2026-06-23 | 4 lines modified | Set notification panel content background to white
